import dayjs from 'dayjs';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DashboardStatistics } from '~/api/statistics/types';

import { Title } from '~/components';
import { Field, Select } from '~/components/Form';
import { clsx } from '~/styles/utils';

import { chartStyle, chartSummaryStyle, chartWrapperStyle, hasSummaryStyle, headingStyle, wrapperStyle } from './Chart.css';

type Props = {
  children?: ReactNode;
  setYear: (year: number) => void;
  statistics: DashboardStatistics;
  summary?: ReactNode;
  title: string;
  year: number;
};

export const ChartWrapper = ({ children, setYear, statistics, summary, title, year }: Props) => {
  const { t } = useTranslation(['dashboard']);

  const yearOptions = useMemo(
    () =>
      Object.keys(statistics.barChartDataByYear)
        .map((year) => ({ value: +year, label: year }))
        .sort((a, b) => b.value - a.value),
    [statistics.barChartDataByYear],
  );

  return (
    <section className={wrapperStyle}>
      <div className={headingStyle}>
        <Title as="h2" styleAs="h3">
          {title}
        </Title>

        {yearOptions.length > 1 && (
          <Field.Root>
            <Field.Label srOnly>{t('dashboard:charts.yearSelector', { title })}</Field.Label>
            <Select.Root onValueChange={(year) => setYear(year ?? dayjs().year())} value={year}>
              <Select.Button />
              <Select.List>
                {yearOptions.map(({ label, value }) => (
                  <Select.Item key={value} value={value}>
                    {label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>
          </Field.Root>
        )}
      </div>

      <div className={clsx(chartWrapperStyle, !!summary && hasSummaryStyle)}>
        {!!summary && <div className={chartSummaryStyle}>{summary}</div>}

        <div className={chartStyle}>{children}</div>
      </div>
    </section>
  );
};
